import React, { useEffect, useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  Briefcase,
  Star,
  Mail,
  Brain,
  ArrowRight,
  CheckCircle,
} from 'lucide-react'

const FeaturesSection = () => {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const [activeTab, setActiveTab] = useState(0)

  const features = [
    {
      id: 'lead-management',
      icon: Briefcase,
      title: '💼 Lead Management',
      subtitle: 'Lead Management & Public Profile Access',
      description:
        'Enable seamless lead capture through a public business profile accessible from anywhere.',
      highlights: [
        'Universal Public Profile Access',
        'Smart Lead Capture',
        'Intelligent Lead Table',
        'Real-Time Lead Tracking',
        'Analytics & Lead Performance',
       
      ],
      color: 'from-yellow-500 to-orange-600',
      gradient: 'from-yellow-50 to-orange-50',
    },
    {
      id: 'review-management',
      icon: Star,
      title: '⭐ Business Analytics',
      subtitle: 'Data-driven visibility for smarter business growth',
      description:
        'Get a powerful analytics dashboard that shows how customers interact with your business profile—all in one place.',
      highlights: [
        'Visitor & Traffic Insights',
        'Link Click & Engagement Tracking',
        'Audience Behavior Analysis',
        'Graphical Data Visualization',
        'Performance Analytics',
        'Smart Business Decisions',
      ],
      color: 'from-yellow-500 to-orange-600',
      gradient: 'from-yellow-50 to-orange-50',
    },
    {
      id: 'email-signature',
      icon: Mail,
      title: '🌐 Creative Public Business Profile',
      subtitle: 'All your business links in one place',
      description:
        'Create a powerful public profile that showcases your business and makes it easy for customers to connect, trust, and take action.',
      highlights: [
        'Custom business profile with branding',
        'Website and custom link integration',
        'Google Maps location link',
        'Google Review link to boost reviews',
        'All social media links in one profile',
        'UPI payment ID integration',
      ],
      color: 'from-green-500 to-teal-600',
      gradient: 'from-green-50 to-teal-50',
    },
   
  ]

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-white py-20 dark:bg-gray-900"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(59, 130, 246, 0.3) 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        ></div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="mb-16 text-center"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="mb-6 text-4xl font-bold text-gray-900 md:text-5xl dark:text-white">
            Powerful <span className="gradient-text">Features</span>
          </h2>
          <p className="mx-auto max-w-3xl text-xl text-gray-600 dark:text-gray-400">
            Discover the advanced features that make FiindIt the ultimate digital
            networking platform for modern professionals and teams.
          </p>
        </motion.div>

        {/* Feature Tabs */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-4">
            {features.map((feature, index) => (
              <motion.button
                key={feature.id}
                onClick={() => setActiveTab(index)}
                className={`rounded-xl px-6 py-3 font-medium transition-all duration-300 ${activeTab === index
                  ? ' bg-blue-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {feature.title}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Feature Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2"
          >
            {/* Left Side - Feature Details */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div
                  className={`h-16 w-16 bg-gradient-to-br mb-6 flex items-center justify-center rounded-2xl`}
                >
                  {features[activeTab] &&
                    React.createElement(features[activeTab].icon, {
                      className: 'w-8 h-8 text-white',
                    })}
                </div>

                <h3 className="mb-4 text-3xl font-bold text-gray-900 dark:text-white">
                  {features[activeTab]?.title}
                </h3>

                <p className="text-primary-600 dark:text-primary-400 mb-6 text-lg font-medium">
                  {features[activeTab]?.subtitle}
                </p>

                <p className="mb-8 text-lg text-gray-600 dark:text-gray-400">
                  {features[activeTab]?.description}
                </p>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {features[activeTab]?.highlights.map((highlight, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center space-x-3"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                    >
                      <CheckCircle className="h-5 w-5 flex-shrink-0 text-green-500" />
                      <span className="text-gray-700 dark:text-gray-300">
                        {highlight}
                      </span>
                    </motion.div>
                  ))}
                </div>

                <motion.button
                  className="btn-primary mt-8 flex items-center space-x-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Learn More</span>
                  <ArrowRight className="h-5 w-5" />
                </motion.button>
              </motion.div>
            </div>

            {/* Right Side - Feature Mockup */}
            <motion.div
              className={`feature-card rounded-3xl bg-gradient-to-br p-8 dark:from-gray-800 dark:to-gray-700`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="rounded-2xl bg-white p-6 shadow-lg dark:bg-gray-900">
                {/* Mockup Header */}
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`h-10 w-10 bg-gradient-to-br flex items-center justify-center rounded-lg`}
                    >
                      {features[activeTab]?.icon &&
                        React.createElement(features[activeTab].icon, {
                          className: 'w-5 h-5 text-white',
                        })}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {features[activeTab]?.title.split(' ')[1]}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Dashboard
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="h-3 w-3 rounded-full bg-green-500"></div>
                    <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                    <div className="h-3 w-3 rounded-full bg-red-500"></div>
                  </div>
                </div>

                {/* Mockup Content */}
                <div className="space-y-4">
                  {[1, 2, 3].map((item) => (
                    <motion.div
                      key={item}
                      className="flex items-center justify-between rounded-lg bg-gray-50 p-3 dark:bg-gray-800"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 + item * 0.1 }}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 rounded-full bg-gray-300 dark:bg-gray-600"></div>
                        <div>
                          <div className="h-3 w-24 rounded bg-gray-300 dark:bg-gray-600"></div>
                          <div className="mt-1 h-2 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
                        </div>
                      </div>
                      <div className="bg-primary-500 h-6 w-12 rounded-full"></div>
                    </motion.div>
                  ))}
                </div>

                {/* Stats */}
                <div className="mt-6 grid grid-cols-3 gap-4">
                  {[
                    { label: 'Total', value: '1,247' },
                    { label: 'Active', value: '892' },
                    { label: 'Growth', value: '+23%' },
                  ].map((stat, index) => (
                    <motion.div
                      key={index}
                      className="text-center"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                    >
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        {stat.value}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {stat.label}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>

        {/* Bottom CTA */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="mb-6 text-2xl font-bold text-gray-900 md:text-3xl dark:text-white">
            Ready to Experience These Features?
          </h3>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-gray-600 dark:text-gray-400">
            Start using FiindIt today and discover how these powerful features
            can transform your networking experience.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Link to="/app/register">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary px-8 py-4 text-lg"
            >
              Start Free Trial
            </motion.button>
            </Link>
            
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default FeaturesSection